import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { usePaginatedQuery, useQuery } from "convex/react"
import { BellPlus, Bookmark } from "lucide-react"
import { toast } from "sonner"
import { api } from "../../convex/_generated/api"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import Footer from "@/components/footer-1"
import { jobWorkModeLabel } from "@/lib/employment"
import { errorMessage } from "@/lib/errors"
import { formatSalary } from "@/lib/salary"
import {
  HIRING_REGION_PRESETS,
  matchesHiringRegions,
  parseRegions,
  toggleRegion,
} from "@/lib/hiring-regions"
import { Chip } from "@/components/chips"
import { EmptyState } from "@/components/empty-state"
import { EntityRow } from "@/components/entity-row"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  JOBS_BROWSE_DEFAULTS,
  jobsBrowseHasFilters,
  jobsBrowseShowFilters,
  parseJobsBrowseSearch,
  serializeJobsBrowseSearch,
  ShareJobButton,
  useJobAlerts,
  useSavedJobs,
  type JobsBrowseSearchState,
} from "@/features/jobs"

export const Route = createFileRoute("/jobs_/browse/")({
  // Search/type/regions/sort are canonical URL state: deep links and
  // back/forward restore the exact board view (PR11).
  validateSearch: parseJobsBrowseSearch,
  component: JobsBoardPage,
})

export function JobsBoardPage() {
  const listing = useQuery(api.jobs.browseMeta)
  const {
    results: jobs,
    status: paginationStatus,
    loadMore,
  } = usePaginatedQuery(api.jobs.listPublicPage, {}, { initialNumItems: 30 })
  const browse = { ...JOBS_BROWSE_DEFAULTS, ...Route.useSearch() }
  const navigate = useNavigate({ from: Route.fullPath })
  const setBrowse = (patch: Partial<JobsBrowseSearchState>) => {
    void navigate({
      search: serializeJobsBrowseSearch({ ...browse, ...patch }) as never,
      replace: true,
    })
  }
  const search = browse.q
  const employmentType = browse.type
  const regionFilter = browse.regions
  const sort = browse.sort
  const setSearch = (q: string) => setBrowse({ q })
  const setEmploymentType = (type: string) => setBrowse({ type })
  const setRegionFilter = (regions: string) => setBrowse({ regions })
  const setSort = (next: JobsBrowseSearchState["sort"]) => setBrowse({ sort: next })

  const isAnonymousPreview = listing?.topOnly ?? false
  const showFilters = jobsBrowseShowFilters(listing?.topOnly)

  const employmentTypes = useMemo(() => {
    if (!jobs) return []
    return Array.from(new Set(jobs.map((job) => job.employmentType))).sort()
  }, [jobs])

  const filteredJobs = useMemo(() => {
    if (!jobs) return []
    if (!showFilters) return jobs
    const q = search.trim().toLowerCase()
    const regions = parseRegions(regionFilter)
    const filtered = jobs.filter((job) => {
      if (employmentType !== "all" && job.employmentType !== employmentType) {
        return false
      }
      if (!matchesHiringRegions(job, regions)) return false
      if (!q) return true
      return (
        job.title.toLowerCase().includes(q) ||
        job.employerName.toLowerCase().includes(q) ||
        job.location.toLowerCase().includes(q) ||
        job.skills.some((skill) => skill.toLowerCase().includes(q))
      )
    })
    return [...filtered].sort((a, b) =>
      sort === "newest"
        ? b._creationTime - a._creationTime
        : a._creationTime - b._creationTime,
    )
  }, [
    jobs,
    search,
    employmentType,
    regionFilter,
    sort,
    showFilters,
  ])

  const hasJobs = (jobs?.length ?? 0) > 0
  const hasFilters = showFilters && jobsBrowseHasFilters(browse)

  const saved = useSavedJobs()
  const jobAlerts = useJobAlerts()
  const [savingAlert, setSavingAlert] = useState(false)

  // Client-side filters only see loaded pages — while any filter is active,
  // keep paging until the whole board is in, so search misses nothing.
  useEffect(() => {
    if (!hasFilters) return
    if (paginationStatus === "CanLoadMore") loadMore(60)
  }, [hasFilters, paginationStatus, loadMore])

  const clearFilters = () => {
    setBrowse({ q: "", type: "all", regions: "" })
  }

  const toggleSave = async (jobId: (typeof filteredJobs)[number]["_id"]) => {
    try {
      const result = await saved.toggle(jobId)
      toast.success(result.saved ? "Saved — find it under Saved in your desk" : "Removed from saved jobs")
    } catch (e) {
      toast.error(errorMessage(e, "Could not save job"))
    }
  }

  const createAlertFromFilters = async () => {
    if (savingAlert) return
    setSavingAlert(true)
    try {
      const result = await jobAlerts.create({
        q: search.trim() || undefined,
        employmentType: employmentType !== "all" ? employmentType : undefined,
        regions: parseRegions(regionFilter),
      })
      toast.success(
        result.created
          ? "Job alert saved — you'll be emailed when new roles match"
          : "You already have an alert for this search",
      )
    } catch (e) {
      toast.error(errorMessage(e, "Could not create alert"))
    } finally {
      setSavingAlert(false)
    }
  }

  return (
    <div className="mkt-page home-wf">
      <div className="mkt-page-shell">
        <h1 className="mkt-page-title">Open roles in HSR</h1>
        <p className="mkt-page-sub">
          {listing?.topOnly
            ? `Today’s top ${listing.previewLimit} roles — rebuilt every day. Log in to browse the full board; applying needs ₹2,500/year access.`
            : "Browse free. Signing up costs nothing — applying needs ₹2,500/year access."}{" "}
          <Link
            to="/hackathons"
            className="font-bold text-[color:var(--wf-lime-text)] underline-offset-4 hover:underline"
          >
            Also browse hackathons →
          </Link>
        </p>

        {listing === undefined || paginationStatus === "LoadingFirstPage" ? (
          <div className="mt-8 space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : !hasJobs ? (
          <p className="mt-8 text-[color:var(--wf-mute)]">No open jobs yet.</p>
        ) : (
          <>
            {showFilters && (
              <>
                <div className="mkt-filters">
                  <Input
                    placeholder="Search title, company, location, skills…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-xs"
                  />
                  <Select value={employmentType} onValueChange={setEmploymentType}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {employmentTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={sort}
                    onValueChange={(value) =>
                      setSort(value as JobsBrowseSearchState["sort"])
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="oldest">Oldest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-[0.68rem] font-bold tracking-[0.06em] text-[color:var(--wf-mute)] uppercase">
                    Area
                  </span>
                  {HIRING_REGION_PRESETS.map((region) => {
                    const active = parseRegions(regionFilter).includes(region)
                    return (
                      <button
                        key={region}
                        type="button"
                        onClick={() =>
                          setRegionFilter(toggleRegion(regionFilter, region))
                        }
                        className={cn(
                          "rounded-full border px-3 py-1 text-sm font-semibold transition-colors",
                          active
                            ? "border-transparent bg-[rgb(196_240_75/0.55)] text-[color:var(--wf-ink)]"
                            : "border-[rgb(12_15_20/0.12)] bg-white text-[color:var(--wf-mute)] hover:bg-[rgb(243_251_227/0.7)]",
                        )}
                      >
                        {region}
                      </button>
                    )
                  })}
                  {regionFilter && (
                    <button
                      type="button"
                      className="text-xs font-bold text-[color:var(--wf-mute)] underline-offset-2 hover:underline"
                      onClick={() => setRegionFilter("")}
                    >
                      Clear area
                    </button>
                  )}
                </div>
              </>
            )}

            <div
              className={cn(
                "flex flex-wrap items-center justify-between gap-2",
                isAnonymousPreview ? "mt-8" : "mt-4",
              )}
            >
              <p className="text-sm text-[color:var(--wf-mute)]">
                {isAnonymousPreview ? (
                  <>
                    Top {filteredJobs.length} today
                  </>
                ) : (
                  <>
                    {filteredJobs.length} open role
                    {filteredJobs.length === 1 ? "" : "s"}
                  </>
                )}
              </p>
              {saved.isSeeker && (
                <button
                  type="button"
                  className="mkt-row-action inline-flex items-center gap-1.5"
                  disabled={savingAlert}
                  onClick={() => void createAlertFromFilters()}
                >
                  <BellPlus className="size-4" aria-hidden />
                  {savingAlert ? "Saving alert…" : "Alert me about new roles like this"}
                </button>
              )}
            </div>

            <div className="mt-2">
              {filteredJobs.length === 0 ? (
                <EmptyState
                  title="No jobs match your filters."
                  action={
                    hasFilters
                      ? { label: "Clear filters", onClick: clearFilters }
                      : undefined
                  }
                />
              ) : (
                filteredJobs.map((job) => {
                  const companyLabel =
                    job.company?.companyName ?? job.employerName
                  const salary = formatSalary(job)
                  return (
                    <EntityRow
                      key={job._id}
                      to="/jobs/$jobId"
                      params={{ jobId: job._id }}
                      mark={{
                        name: companyLabel,
                        imageUrl: job.company?.companyLogoUrl,
                      }}
                      title={job.title}
                      chips={
                        <>
                          <Chip emphasis>{job.employmentType}</Chip>
                          {job.featured && <Chip emphasis>Featured</Chip>}
                          {job.workMode && (
                            <Chip>{jobWorkModeLabel(job.workMode)}</Chip>
                          )}
                          {salary && <Chip>{salary}</Chip>}
                        </>
                      }
                      meta={`${job.employerName} · ${job.location}`}
                      regions={job.remoteRegions ?? undefined}
                      tags={{ items: job.skills, max: 3 }}
                      action={
                        <div className="flex items-center gap-0.5 self-start">
                          <ShareJobButton
                            jobId={job._id}
                            title={job.title}
                            companyName={companyLabel}
                            variant="icon"
                          />
                          {saved.isSeeker && (
                            <button
                              type="button"
                              className="mkt-row-action self-start"
                              aria-pressed={saved.isSaved(job._id)}
                              aria-label={
                                saved.isSaved(job._id)
                                  ? `Remove ${job.title} from saved jobs`
                                  : `Save ${job.title}`
                              }
                              onClick={() => void toggleSave(job._id)}
                            >
                              <Bookmark
                                className={cn(
                                  "size-4",
                                  saved.isSaved(job._id) &&
                                    "fill-[color:var(--wf-lime-text)] text-[color:var(--wf-lime-text)]",
                                )}
                                aria-hidden
                              />
                            </button>
                          )}
                        </div>
                      }
                    />
                  )
                })
              )}
            </div>

            {isAnonymousPreview && (
              <div className="mkt-browse-gate mt-6">
                <p className="mkt-browse-gate__title">
                  More roles are available to members
                </p>
                <p className="mkt-browse-gate__copy">
                  Log in to see the full list. Applying still needs ₹2,500/year
                  access.
                </p>
                <div className="mkt-browse-gate__actions">
                  <Link
                    to="/login"
                    search={{ returnTo: "/jobs/browse" }}
                    className="home-wf-btn home-wf-btn--lime"
                  >
                    See more
                  </Link>
                  <Link
                    to="/jobs/join"
                    search={{ returnTo: "/jobs/browse" }}
                    className="home-wf-btn home-wf-btn--outline"
                  >
                    Join free
                  </Link>
                </div>
              </div>
            )}
            {!isAnonymousPreview && paginationStatus === "CanLoadMore" && (
              <div className="mt-6">
                <Button type="button" variant="outline" onClick={() => loadMore(30)}>
                  Load more roles
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  )
}
